import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, MessageCircle, FileText, Send } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import type { VagaRH, FormCandidatura } from '../types/rh';
import ChatModal from '../components/ChatModal';

const VagaDetalhePage = () => {
  const { id } = useParams<{ id: string }>();
  const [vaga, setVaga] = useState<VagaRH | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [formData, setFormData] = useState<FormCandidatura>({
    nome: '',
    email: '',
    telefone: '',
    vaga_id: id || '',
    origem_lead: 'site'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      carregarVaga();
    }
  }, [id]);

  const carregarVaga = async () => {
    try {
      if (!id) return;
      
      const { data, error } = await supabase
        .from('vagas_rh')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setVaga(data);
    } catch (error) {
      console.error('Erro ao carregar vaga:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCandidatura = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vaga) return;

    setSubmitting(true);
    try {
      let curriculoUrl = '';
      
      // Upload do currículo se fornecido
      if (formData.curriculo) {
        const fileExt = formData.curriculo.name.split('.').pop();
        const fileName = `${Date.now()}-${formData.nome.replace(/\s+/g, '-')}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('curriculos')
          .upload(fileName, formData.curriculo);

        if (uploadError) throw uploadError;
        curriculoUrl = uploadData.path;
      }

      // Inserir candidato
      const { error } = await supabase
        .from('candidatos_rh')
        .insert({
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          curriculo_url: curriculoUrl,
          vaga_id: vaga.id,
          origem_lead: formData.origem_lead,
          tipo_cadastro: 'candidato'
        });

      if (error) throw error;

      alert('Candidatura enviada com sucesso! Entraremos em contato em breve.');
      setShowForm(false);
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        vaga_id: id || '',
        origem_lead: 'site'
      });
    } catch (error) {
      console.error('Erro ao enviar candidatura:', error);
      alert('Erro ao enviar candidatura. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const getLocalidadeBadgeColor = (tipo: string) => {
    switch (tipo) {
      case 'remoto': return 'bg-green-100 text-green-800';
      case 'hibrido': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando vaga...</p>
        </div>
      </div>
    );
  }

  if (!vaga) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Vaga não encontrada</h2>
          <Link to="/vagas" className="text-orange-600 hover:text-orange-800">
            Voltar para vagas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header com Logo */}
      <header className="absolute top-0 left-0 right-0 z-10 p-6">
        <div className="flex items-center">
          <img 
            src="/lovable-uploads/1f6dcb92-5dfc-4a75-aa2e-c3251eb28c93.png" 
            alt="Fox Vendas Logo" 
            className="h-12 w-auto"
          />
        </div>
      </header>

      {/* Header Principal */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 py-8 pt-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center mb-6">
            <Link to="/vagas" className="text-white hover:text-gray-200 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </Link>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-4">
                {vaga.titulo}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-200">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {vaga.localidade}
                </div>
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  {vaga.tipo_vaga}
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Publicada em {new Date(vaga.created_at).toLocaleDateString('pt-BR')}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getLocalidadeBadgeColor(vaga.tipo_localidade)}`}>
                  {vaga.tipo_localidade}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2 space-y-8">
            {/* Descrição */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Descrição da Vaga</h2>
              <div className="prose max-w-none text-gray-600">
                {vaga.descricao.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Benefícios */}
            {vaga.beneficios && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Benefícios</h2>
                <div className="prose max-w-none text-gray-600">
                  {vaga.beneficios.split('\n').map((item, index) => (
                    <p key={index} className="mb-2">
                      • {item}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Requisitos */}
            {vaga.requisitos && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Requisitos</h2>
                <div className="prose max-w-none text-gray-600">
                  {vaga.requisitos.split('\n').map((item, index) => (
                    <p key={index} className="mb-2">
                      • {item}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - CTAs */}
          <div className="space-y-6">
            {/* CTA Candidatar */}
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Interessado na vaga?</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  Candidatar-se
                </button>
                
                <div className="text-center text-gray-500 text-sm">ou</div>
                
                <button
                  onClick={() => setShowChat(true)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Conversar Agora
                </button>
              </div>
            </div>

            {/* Informações da empresa */}
            <div className="bg-gradient-to-br from-gray-50 to-orange-50 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Sobre a Fox Vendas</h3>
              <p className="text-gray-600 text-sm mb-4">
                Líder no mercado imobiliário de Campinas, oferecemos um ambiente de crescimento profissional com foco em resultados e desenvolvimento humano.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                <div>• 4+ anos no mercado</div>
                <div>• 200+ imóveis vendidos</div>
                <div>• 30+ profissionais</div>
                <div>• 2 unidades ativas</div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="font-medium text-gray-900 mb-2">Entre em contato:</p>
                <p className="text-sm text-gray-700 font-medium">Aleska Duarte</p>
                <p className="text-sm text-gray-600">📧 aleska@foxvendas.com.br</p>
                <p className="text-sm text-gray-600">📱 (19) 98606-7515</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Candidatar-se para {vaga.titulo}</h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleSubmitCandidatura} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currículo (opcional)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setFormData({...formData, curriculo: e.target.files?.[0]})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos aceitos: PDF, DOC, DOCX (máx. 10MB)
                  </p>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal do Chat com IA */}
      <ChatModal 
        vaga={{ id: vaga.id, titulo: vaga.titulo }}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />
    </div>
  );
};

export default VagaDetalhePage;